import React, { useEffect, useState } from "react";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import { Ingredient, SemiFinishedProduct, RecipeLinePayload, Machine, ProductionPhaseType, ProductionUnit, UnitType } from "../../../types/mes";
import { isAbsoluteRecipe } from "../../../utils/recipeQuantity";
import { ToastContainer, toast } from "react-toastify";
import PhaseEditor, { PhaseRow, phasesToRows, rowsToPayload } from "../../../components/MES/PhaseEditor";
import NumberInput from "../../../components/DataEntry/NumberInput";

interface EditableLine {
  ingredient_id: number;
  ingredient_name: string;
  ingredient_type: string;
  /** Pekarska receptura (QuantityBasis 'baker_pct'). */
  baker_percentage: number;
  /** Slastičarska receptura (QuantityBasis 'absolute'). */
  quantity: number;
  unit_type_id?: number;
  sort_order: number;
  notes: string;
}

const typeLabel: Record<string, string> = {
  starter: "Starter",
  enzyme: "Enzim",
  seed_mix: "Mješavina sjemena",
  other: "Ostalo",
};

const SemiFinished = () => {
  savePathToLocalStorage();

  const [items, setItems] = useState<SemiFinishedProduct[]>([]);
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [productionUnits, setProductionUnits] = useState<ProductionUnit[]>([]);
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [unitFilter, setUnitFilter] = useState<string>("");
  const [phaseTypes, setPhaseTypes] = useState<ProductionPhaseType[]>([]);
  const [phaseRows, setPhaseRows] = useState<PhaseRow[]>([]);
  const [selected, setSelected] = useState<SemiFinishedProduct | null>(null);
  const [lines, setLines] = useState<EditableLine[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Slastičarski poluproizvod ima recepturu u gramima i listovima; pekarski u PEK %.
  // Prati odabrani pogon, a ne osnovu spremljenu na recepturi: kad se pogon
  // promijeni, tablica se prebaci odmah, bez zatvaranja i vraćanja u modal.
  // Backend pri spremanju pogona povlači osnovu recepture za njim.
  const selectedUnitCode = productionUnits.find((u) => u.ID === selected?.ProductionUnit)?.Code;
  const isAbsolute = selectedUnitCode
    ? selectedUnitCode === "slasticarna"
    : isAbsoluteRecipe(selected?.recipe);

  // Create new semi-finished product
  const emptyNew: Partial<SemiFinishedProduct> = { Name: "", SemiFinishedType: "starter" as SemiFinishedProduct["SemiFinishedType"], IsActive: true };
  const [createOpen, setCreateOpen] = useState(false);
  const [newItem, setNewItem] = useState<Partial<SemiFinishedProduct>>({ ...emptyNew });
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    if (!newItem.Name || !String(newItem.Name).trim()) {
      toast.error("Naziv poluproizvoda je obavezan");
      return;
    }
    setCreating(true);
    try {
      await mesApi.createSemiFinishedProduct({
        Name: String(newItem.Name).trim(),
        SemiFinishedType: newItem.SemiFinishedType || "starter",
        ProductionUnit: newItem.ProductionUnit ?? null,
        IsActive: true,
      });
      toast.success("Poluproizvod dodan");
      setCreateOpen(false);
      setNewItem({ ...emptyNew });
      loadAll();
    } catch (err: any) {
      toast.error("Greška pri dodavanju: " + err.message);
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, [unitFilter]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [sf, ing, mach, types, units, unitTypeList] = await Promise.all([
        mesApi.getSemiFinishedProducts({
          is_active: true,
          production_unit: unitFilter ? Number(unitFilter) : undefined,
        }),
        mesApi.getIngredients({ is_active: true }),
        mesApi.getMachines(),
        mesApi.getProductionPhaseTypes(),
        mesApi.getProductionUnits(),
        mesApi.getUnitTypes(),
      ]);
      setItems(sf);
      setIngredients(ing);
      setMachines(mach);
      setPhaseTypes(types);
      setProductionUnits(units);
      setUnitTypes(unitTypeList);
    } catch (err: any) {
      toast.error("Greška pri učitavanju: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const openItem = async (item: SemiFinishedProduct) => {
    try {
      const full = await mesApi.getSemiFinishedProduct(item.ID);
      setSelected(full);
      setPhaseRows(phasesToRows(full.ProductionPhase));
      const recipeLines = full.recipe?.RecipeIngredient_RecipeToRecipeIngredient_Recipe || [];
      setLines(
        recipeLines.map((ri, idx) => ({
          ingredient_id: ri.Ingredient || 0,
          ingredient_name: ri.Ingredient_IngredientToRecipeIngredient?.IngredientName || "",
          ingredient_type: ri.Ingredient_IngredientToRecipeIngredient?.IngredientTypeName || "",
          baker_percentage: Number(ri.BakerPercentage) || 0,
          quantity: Number(ri.Quantity) || 0,
          unit_type_id: ri.UnitType ?? undefined,
          sort_order: ri.SortOrder ?? idx + 1,
          notes: ri.Notes || "",
        }))
      );
    } catch (err: any) {
      toast.error(err.message);
    }
  };

  const closeModal = () => {
    setSelected(null);
    setLines([]);
    setPhaseRows([]);
  };

  const handleScheduleFieldChange = (
    field: keyof SemiFinishedProduct,
    value: string | number | null
  ) => {
    if (!selected) return;
    setSelected({ ...selected, [field]: value });
  };

  const getIngredientId = (i: Ingredient) => i.ID;

  const updateLine = (index: number, field: keyof EditableLine, value: unknown) => {
    const next = [...lines];
    next[index] = { ...next[index], [field]: value } as EditableLine;
    if (field === "ingredient_id" && value) {
      const ing = ingredients.find((i) => getIngredientId(i) === Number(value));
      if (ing) {
        next[index].ingredient_name = ing.IngredientName;
        next[index].ingredient_type = ing.IngredientTypeName || "";
      }
    }
    setLines(next);
  };

  const addLine = () => {
    setLines([
      ...lines,
      {
        ingredient_id: 0,
        ingredient_name: "",
        ingredient_type: "",
        baker_percentage: 0,
        quantity: 0,
        unit_type_id: unitTypes.find((u) => u.Code === "g")?.ID,
        sort_order: lines.length + 1,
        notes: "",
      },
    ]);
  };

  const removeLine = (index: number) => {
    setLines(lines.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!selected) return;
    const invalid = isAbsolute
      ? lines.filter((l) => !l.ingredient_id || !(l.quantity > 0) || !l.unit_type_id)
      : lines.filter((l) => !l.ingredient_id || l.baker_percentage <= 0);
    if (invalid.length) {
      toast.error(
        isAbsolute
          ? "Sve linije moraju imati sirovinu, količinu > 0 i jedinicu"
          : "Sve linije moraju imati sirovinu i PEK% > 0"
      );
      return;
    }
    setSaving(true);
    try {
      const payload: RecipeLinePayload[] = lines.map((l, idx) => ({
        LineType: "ingredient",
        Ingredient: l.ingredient_id,
        ...(isAbsolute
          ? { Quantity: l.quantity, UnitType: l.unit_type_id ?? null }
          : { BakerPercentage: l.baker_percentage }),
        SortOrder: idx + 1,
        Notes: l.notes || null,
      }));
      await mesApi.updateSemiFinishedProduct(selected.ID, {
        ProductionUnit: selected.ProductionUnit ?? null,
        ScheduleMode: selected.ScheduleMode ?? "same_day",
        ProductionOffsetDays:
          selected.ScheduleMode === "offset_days"
            ? selected.ProductionOffsetDays ?? 1
            : null,
        ProductionWeekday:
          selected.ScheduleMode === "weekday"
            ? selected.ProductionWeekday ?? 5
            : null,
      });
      await mesApi.setSemiFinishedPhases(selected.ID, rowsToPayload(phaseRows));
      await mesApi.updateSemiFinishedRecipeIngredients(selected.ID, payload);
      toast.success("Poluproizvod i recept spremljeni");
      closeModal();
      loadAll();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mes-page semi-finished-page">
      <Header title="MES - Poluproizvodi" />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content">
          {loading ? (
            <div className="mes-section loading-state">Učitavanje...</div>
          ) : (
            <div className="mes-section">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                <h3 style={{ margin: 0 }}>Poluproizvodi</h3>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <select
                    className="select-classic"
                    value={unitFilter}
                    onChange={(e) => setUnitFilter(e.target.value)}
                    style={{ maxWidth: 180 }}
                  >
                    <option value="">Svi pogoni</option>
                    {productionUnits.map((u) => (
                      <option key={u.ID} value={u.ID}>{u.Name}</option>
                    ))}
                  </select>
                  <button
                    className="btn-submit"
                    onClick={() => {
                      // Pekara je predodabrana kao i kod proizvoda; ako je lista
                      // filtrirana po pogonu, novi poluproizvod ide u taj pogon.
                      const defaultUnit = unitFilter
                        ? Number(unitFilter)
                        : productionUnits.find((u) => u.Code === "pekara")?.ID ?? null;
                      setNewItem({ ...emptyNew, ProductionUnit: defaultUnit });
                      setCreateOpen(true);
                    }}
                  >
                    + Dodaj poluproizvod
                  </button>
                </div>
              </div>
              <p className="modal-hint" style={{ margin: "0.5rem 0 1rem" }}>
                Starteri, enzimi i mješavine sjemena — svaki ima vlastiti recept od sirovina.
              </p>
              <div className="mes-table-wrap">
                <table className="mes-table">
                <thead>
                  <tr>
                    <th>Naziv</th>
                    <th>Pogon</th>
                    <th>Tip</th>
                    <th>Linija u receptu</th>
                    <th>Mijesanje</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr key={item.ID}>
                      <td style={{ fontWeight: 500 }}>{item.Name}</td>
                      <td>
                        {item.ProductionUnit_SemiFinishedToProductionUnit?.Name ? (
                          <span className={`unit-badge unit-${item.ProductionUnit_SemiFinishedToProductionUnit.Code}`}>
                            {item.ProductionUnit_SemiFinishedToProductionUnit.Name}
                          </span>
                        ) : "—"}
                      </td>
                      <td>{typeLabel[item.SemiFinishedType] || item.SemiFinishedType}</td>
                      <td>{item.line_count ?? 0}</td>
                      <td>{item.MixingTimeMin != null ? `${item.MixingTimeMin} min` : "—"}</td>
                      <td>
                        <button className="btn-sm btn-secondary" onClick={() => openItem(item)}>
                          Uredi
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}

          {selected && (
            <div className="mes-modal-overlay" onClick={closeModal}>
              <div className="mes-modal mes-modal-lg" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <div className="modal-title-group">
                    <div className="modal-icon">🧪</div>
                    <div className="modal-title-text">
                      <h3>{selected.Name}</h3>
                      <p className="modal-subtitle">
                        {typeLabel[selected.SemiFinishedType] || selected.SemiFinishedType} — recept
                      </p>
                    </div>
                  </div>
                  <button className="close-btn" onClick={closeModal}>✕</button>
                </div>
                <div className="modal-body" style={{ maxHeight: "65vh", overflowY: "auto" }}>
                  <div className="form-group" style={{ maxWidth: 260 }}>
                    <label>Pogon</label>
                    <select
                      value={selected.ProductionUnit ?? ""}
                      onChange={(e) =>
                        handleScheduleFieldChange("ProductionUnit", e.target.value ? Number(e.target.value) : null)
                      }
                    >
                      <option value="">— nije odreden —</option>
                      {productionUnits.map((u) => (
                        <option key={u.ID} value={u.ID}>{u.Name}</option>
                      ))}
                    </select>
                  </div>
                  <h4 style={{ marginBottom: "0.75rem" }}>Faze proizvodnje</h4>
                  <div style={{ marginBottom: "1.25rem" }}>
                    <PhaseEditor
                      rows={phaseRows}
                      onChange={setPhaseRows}
                      phaseTypes={phaseTypes}
                      machines={machines}
                    />
                  </div>
                  <h4 style={{ marginBottom: "0.75rem" }}>Raspored proizvodnje</h4>
                  <p className="modal-hint" style={{ marginBottom: "0.75rem" }}>
                    Kada se ovaj poluproizvod radi u odnosu na gotov proizvod koji ga koristi.
                  </p>
                  <div className="form-grid-2" style={{ marginBottom: "1.25rem" }}>
                    <div className="form-group">
                      <label>Pravilo</label>
                      <select
                        value={selected.ScheduleMode ?? "same_day"}
                        onChange={(e) =>
                          handleScheduleFieldChange("ScheduleMode", e.target.value)
                        }
                      >
                        <option value="same_day">Isti dan</option>
                        <option value="offset_days">Dan(a) prije</option>
                        <option value="weekday">Određeni dan u tjednu</option>
                      </select>
                    </div>
                    {selected.ScheduleMode === "offset_days" && (
                      <div className="form-group">
                        <label>Broj dana prije</label>
                        <input
                          type="number"
                          min={0}
                          value={String(selected.ProductionOffsetDays ?? 1)}
                          onChange={(e) =>
                            handleScheduleFieldChange(
                              "ProductionOffsetDays",
                              Number(e.target.value)
                            )
                          }
                        />
                      </div>
                    )}
                    {selected.ScheduleMode === "weekday" && (
                      <div className="form-group">
                        <label>Dan u tjednu</label>
                        <select
                          value={String(selected.ProductionWeekday ?? 5)}
                          onChange={(e) =>
                            handleScheduleFieldChange(
                              "ProductionWeekday",
                              Number(e.target.value)
                            )
                          }
                        >
                          <option value="1">Ponedjeljak</option>
                          <option value="2">Utorak</option>
                          <option value="3">Srijeda</option>
                          <option value="4">Četvrtak</option>
                          <option value="5">Petak</option>
                          <option value="6">Subota</option>
                          <option value="0">Nedjelja</option>
                        </select>
                      </div>
                    )}
                  </div>
                  <h4 style={{ marginBottom: "0.75rem" }}>Recept (sirovine)</h4>
                  <div className="mes-table-wrap">
                    <table className="mes-table ingredient-edit-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Sirovina</th>
                        <th>Tip</th>
                        {isAbsolute ? (
                          <>
                            <th style={{ width: 110 }}>Kolicina</th>
                            <th style={{ width: 100 }}>Jedinica</th>
                          </>
                        ) : (
                          <th>PEK %</th>
                        )}
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.map((line, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>
                            <select
                              value={line.ingredient_id || ""}
                              onChange={(e) => updateLine(idx, "ingredient_id", Number(e.target.value))}
                              style={{ width: "100%" }}
                            >
                              <option value="">-- Odaberi --</option>
                              {ingredients.map((i) => (
                                <option key={getIngredientId(i)} value={getIngredientId(i)}>
                                  {i.IngredientName}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td>{line.ingredient_type || "-"}</td>
                          {isAbsolute ? (
                            <>
                              <td>
                                <NumberInput
                                  step="0.001"
                                  value={line.quantity}
                                  onValueChange={(v) => updateLine(idx, "quantity", v)}
                                  style={{ width: "100%" }}
                                />
                              </td>
                              <td>
                                <select
                                  value={line.unit_type_id ?? ""}
                                  onChange={(e) =>
                                    updateLine(idx, "unit_type_id", e.target.value ? Number(e.target.value) : undefined)
                                  }
                                  style={{ width: "100%" }}
                                >
                                  <option value="">—</option>
                                  {unitTypes.map((u) => (
                                    <option key={u.ID} value={u.ID}>{u.Code}</option>
                                  ))}
                                </select>
                              </td>
                            </>
                          ) : (
                            <td>
                              <NumberInput
                                step="0.1"
                                value={line.baker_percentage}
                                onValueChange={(v) => updateLine(idx, "baker_percentage", v)}
                                style={{ width: "100%" }}
                              />
                            </td>
                          )}
                          <td>
                            <button className="btn-sm btn-danger" onClick={() => removeLine(idx)}>✕</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                  <button className="btn-secondary" onClick={addLine} style={{ marginTop: "0.5rem" }}>
                    + Dodaj sirovinu
                  </button>
                </div>
                <div className="modal-footer">
                  <button className="btn-cancel" onClick={closeModal}>Odustani</button>
                  <button className="btn-submit" onClick={handleSave} disabled={saving}>
                    {saving ? "Spremanje..." : "Spremi recept"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {createOpen && (
        <div className="mes-modal-overlay" onClick={() => setCreateOpen(false)}>
          <div className="mes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Novi poluproizvod</h3>
              <button className="modal-close" onClick={() => setCreateOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Naziv *</label>
                <input
                  type="text"
                  value={newItem.Name || ""}
                  onChange={(e) => setNewItem((p) => ({ ...p, Name: e.target.value }))}
                  placeholder="npr. Raženi starter"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Tip *</label>
                <select
                  className="select-classic"
                  value={newItem.SemiFinishedType || "starter"}
                  onChange={(e) => setNewItem((p) => ({ ...p, SemiFinishedType: e.target.value as SemiFinishedProduct["SemiFinishedType"] }))}
                >
                  {Object.entries(typeLabel).map(([val, label]) => (
                    <option key={val} value={val}>{label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Pogon</label>
                <select
                  className="select-classic"
                  value={newItem.ProductionUnit ?? ""}
                  onChange={(e) =>
                    setNewItem((p) => ({ ...p, ProductionUnit: e.target.value ? Number(e.target.value) : null }))
                  }
                >
                  <option value="">— nije odreden —</option>
                  {productionUnits.map((u) => (
                    <option key={u.ID} value={u.ID}>{u.Name}</option>
                  ))}
                </select>
              </div>
              <p className="modal-hint">Recept (sastav) poluproizvoda uređuje se nakon dodavanja.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setCreateOpen(false)} disabled={creating}>Odustani</button>
              <button className="btn-submit" onClick={handleCreate} disabled={creating}>
                {creating ? "Spremam…" : "Dodaj"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer autoClose={5000} position="bottom-right" theme="colored" />
    </div>
  );
};

export default SemiFinished;
